import { MENUS } from '@/constants';
import type { RegieInStoreType } from '@/zustand';

/**
 * Règle de masquage d'un point de menu.
 *
 * `code`   code du menu (constante MENUS.MENU_*). L'URL n'est jamais écrite en dur : elle est
 *          reconstruite comme les redirections de l'app (baseUrlFront + MENUS.LOCATIONS[code]),
 *          donc valide en local ET déployé. L'item ET tous ses sous-items sont masqués.
 * `when`   prédicat évalué sur la régie chargée : renvoie `true` pour masquer.
 * `label`  note lisible facultative (non utilisée par le mécanisme).
 */
export type MenuVisibilityRule = {
  code: string;
  when: (regie: RegieInStoreType) => boolean;
  label?: string;
};

/**
 * Déclaration des règles de masquage des points de menu selon la régie chargée.
 *
 * Pour ajouter un masquage : ajouter une entrée { code, when } en référençant le code du menu
 * (MENUS.MENU_ENCAISSEMENT, MENUS.MENU_LISTES pour toute la section, MENUS.MENU_LISTE_FACTURE pour
 * un seul sous-item, …). L'URL est dérivée de MENUS.LOCATIONS — même source que les redirections.
 *
 * `when` ne reçoit que les champs présents dans le store (RegieInStoreType) :
 * identifiant, titre, ecole, autonome, encaissementPossible, annulationPossible,
 * periodiciteArrete, arreteConfig, … (pas modesReglements).
 */
export const MENU_VISIBILITY_RULES: MenuVisibilityRule[] = [
  // Régie autonome : on masque entièrement le dépliable "Cycles, factures et reçus"
  // et on le remplace par le point de menu "Reçus" seul (déclaré en permanence dans index.tsx).
  {
    code: MENUS.MENU_LISTES,
    when: (regie) => regie.autonome,
    label: 'Régie autonome : masque le dépliable "Cycles, factures et reçus"',
  },
  {
    code: MENUS.MENU_RECUS_AUTONOME,
    when: (regie) => !regie.autonome,
    label: 'Régie non autonome : masque le menu "Reçus" seul (le dépliable est affiché)',
  },
  // Régie autonome : pas de factures, donc ni prélèvements ni relances / impayés.
  {
    code: MENUS.MENU_PRELEVEMENTS,
    when: (regie) => regie.autonome,
    label: 'Régie autonome : masque "Gestion des factures prélevées"',
  },
  {
    code: MENUS.MENU_IMPAYES,
    when: (regie) => regie.autonome,
    label: 'Régie autonome : masque "Relances et impayés"',
  },
];
